// Iter 81 / Phase E0 — Employee detail with tabs: Profile · Documents ·
// Linked User · Generated Documents · Audit.
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    ArrowLeft, PencilSimple, UserPlus, UserMinus, FileArrowUp, Trash, FileText, Eye,
    ShareNetwork, Link as LinkIcon, ClipboardText, Copy,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import { formatDate } from "../../lib/format";

const TABS = ["Profile", "Documents", "Generated Documents", "Linked User", "Audit"];

const Row = ({ k, v }) => (
    <div className="grid grid-cols-3 gap-3 py-2 border-b border-slate-100 last:border-0">
        <div className="text-xs text-slate-500">{k}</div>
        <div className="col-span-2 text-sm text-slate-800">{v || <span className="text-slate-300">—</span>}</div>
    </div>
);

export default function EmployeeDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { can } = useAuth();
    const [emp, setEmp] = useState(null);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState("Profile");
    const [audit, setAudit] = useState([]);
    const [meta, setMeta] = useState({ document_types: [], employee_statuses: [] });
    const [users, setUsers] = useState([]);
    const [linkDialog, setLinkDialog] = useState(false);
    const [pickedUserId, setPickedUserId] = useState("");
    const [genDialog, setGenDialog] = useState(false);
    const [templates, setTemplates] = useState([]);
    const [pickedTemplate, setPickedTemplate] = useState("");
    const [genPreview, setGenPreview] = useState(null);
    const [uploadFile, setUploadFile] = useState(null);
    const [uploadType, setUploadType] = useState("pan");

    // Iter 81.1 / Phase E0.1 — Share / Acceptance state.
    const [shareDialogDoc, setShareDialogDoc] = useState(null);
    const [shareNote, setShareNote] = useState("");
    const [shareTokenInfo, setShareTokenInfo] = useState(null); // { url, expires_at } after issue
    const [trailDoc, setTrailDoc] = useState(null);
    const [trailData, setTrailData] = useState(null);
    // Iter 81.1 — Issue/Effective dates + signatories on generate.
    const [genIssueDate, setGenIssueDate] = useState("");
    const [genEffectiveDate, setGenEffectiveDate] = useState("");
    const [genPreparedBy, setGenPreparedBy] = useState("");
    const [genSignatory, setGenSignatory] = useState("");

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/hr/employee/${id}`);
            setEmp(data);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load employee"));
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        // Defer initial reload via post-commit microtask to satisfy
        // React 19 react-hooks/set-state-in-effect.
        Promise.resolve().then(() => { reload(); });
    }, [reload]);

    const reloadMeta = async () => {
        try {
            const { data } = await api.get("/hr/employee/meta");
            setMeta(data);
        } catch {
            /* meta is optional UX hint — ignore failures silently */
        }
    };

    useEffect(() => {
        // Mirrors the MigrationModeAdmin pattern that satisfies
        // react-hooks/set-state-in-effect on React 19.
        Promise.resolve().then(() => { reloadMeta(); });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadAudit = useCallback(() => {
        api.get(`/hr/employee/${id}/audit`)
            .then(({ data }) => setAudit(data.items || []))
            .catch((err) => toast.error(formatApiError(err, "Failed to load audit")));
    }, [id]);

    useEffect(() => { if (tab === "Audit") loadAudit(); }, [tab, loadAudit]);

    const openLinkDialog = async () => {
        try {
            const { data } = await api.get("/users");
            setUsers(data.items || data || []);
            setLinkDialog(true);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load users"));
        }
    };

    const doLink = async () => {
        if (!pickedUserId) return;
        try {
            await api.post(`/hr/employee/${id}/link-user`, { user_id: pickedUserId });
            toast.success("User linked successfully.");
            setLinkDialog(false);
            setPickedUserId("");
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Link failed"));
        }
    };

    const doUnlink = async () => {
        if (!window.confirm("Unlink this user from the employee?")) return;
        try {
            await api.post(`/hr/employee/${id}/unlink-user`);
            toast.success("Unlinked.");
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Unlink failed"));
        }
    };

    const openGenDialog = async () => {
        try {
            const { data } = await api.get("/hr/document-template/list");
            setTemplates(data.items || []);
            setGenDialog(true);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load templates"));
        }
    };

    const doGenerate = async () => {
        if (!pickedTemplate) return;
        try {
            const { data } = await api.post("/hr/document/generate", {
                template_code: pickedTemplate,
                employee_id: id,
                issue_date: genIssueDate || null,
                effective_date: genEffectiveDate || null,
                prepared_by: genPreparedBy || null,
                authorized_signatory: genSignatory || null,
            });
            toast.success("Document generated.");
            setGenPreview(data);
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Generation failed"));
        }
    };

    // Iter 81.1 / Phase E0.1 — Share + tokenise actions.
    const doShare = async (asToken) => {
        if (!shareDialogDoc) return;
        try {
            const payload = { note: shareNote, generate_token: asToken };
            const { data } = await api.post(`/hr/document/${shareDialogDoc.id}/share`, payload);
            toast.success(asToken ? "Shared + secure link generated." : "Document marked as shared.");
            if (asToken && data.token) {
                setShareTokenInfo({ url: data.token.url, expires_at: data.token.expires_at });
            } else {
                setShareDialogDoc(null);
                setShareNote("");
            }
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Share failed"));
        }
    };

    const openTrail = async (docId) => {
        try {
            const { data } = await api.get(`/hr/document/${docId}/acceptance-trail`);
            setTrailDoc(docId);
            setTrailData(data);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load trail"));
        }
    };

    const doUpload = async () => {
        if (!uploadFile) return toast.error("Pick a file first.");
        const fd = new FormData();
        fd.append("file", uploadFile);
        fd.append("document_type", uploadType);
        try {
            await api.post(`/hr/employee/${id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Uploaded.");
            setUploadFile(null);
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Upload failed"));
        }
    };

    const doDeleteDoc = async (docId) => {
        if (!window.confirm("Delete this document?")) return;
        try {
            await api.delete(`/hr/employee/${id}/documents/${docId}`);
            toast.success("Deleted.");
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Delete failed"));
        }
    };

    const doStatus = async (newStatus) => {
        if (newStatus === emp.status) return;
        const reason = window.prompt(`Reason for transition → ${newStatus}? (optional)`) || "";
        try {
            await api.patch(`/hr/employee/${id}/status`, { status: newStatus, reason });
            toast.success(`Status → ${newStatus}`);
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Status change failed"));
        }
    };

    if (loading || !emp) {
        return <div className="px-6 sm:px-8 py-12 text-sm text-slate-500">Loading…</div>;
    }

    return (
        <div className="px-6 sm:px-8 py-6 max-w-[1400px]" data-testid="employee-detail-page">
            <PageHeader
                eyebrow={`Employee · ${emp.employee_code}`}
                title={emp.full_name}
                description={(
                    <div className="flex flex-wrap items-center gap-2 mt-1">
                        <Badge variant="outline">{emp.employment_category}</Badge>
                        {emp.department && <Badge variant="outline">{emp.department}</Badge>}
                        {emp.designation && <Badge variant="outline">{emp.designation}</Badge>}
                        <Badge data-testid="employee-detail-status" className="bg-slate-100 text-slate-700">{emp.status}</Badge>
                    </div>
                )}
                actions={
                    <div className="flex gap-2 flex-wrap">
                        <Button variant="outline" onClick={() => navigate("/employees")} data-testid="employee-detail-back"><ArrowLeft size={14} className="mr-1" /> Back</Button>
                        <a href={`/employees/${id}/manager-brief/print`} target="_blank" rel="noreferrer">
                            <Button variant="outline" data-testid="employee-detail-manager-brief"><FileText size={14} className="mr-1" /> Manager Brief</Button>
                        </a>
                        {can("employee:write") && (
                            <Button variant="outline" onClick={() => navigate(`/employees/${id}/edit`)} data-testid="employee-detail-edit"><PencilSimple size={14} className="mr-1" /> Edit</Button>
                        )}
                        {can("employee:admin") && (
                            <Button onClick={openGenDialog} data-testid="employee-detail-generate-doc"><FileText size={14} className="mr-1" /> Generate Document</Button>
                        )}
                    </div>
                }
            />

            <div className="mt-6 flex gap-2 border-b border-slate-200" data-testid="employee-detail-tabs">
                {TABS.map((t) => (
                    <button
                        key={t}
                        data-testid={`tab-${t.toLowerCase().replace(/\s+/g, '-')}`}
                        onClick={() => setTab(t)}
                        className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === t ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-700"}`}
                    >
                        {t}
                    </button>
                ))}
            </div>

            <div className="mt-6">
                {tab === "Profile" && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <section className="bg-white rounded-xl border border-slate-200 p-5">
                            <h3 className="text-sm font-semibold mb-3 text-slate-700">Identity</h3>
                            <Row k="Employee Code" v={<span className="font-mono">{emp.employee_code}</span>} />
                            <Row k="Personal Email" v={emp.personal_email} />
                            <Row k="Official Email" v={emp.official_email} />
                            <Row k="Mobile" v={emp.mobile} />
                            <Row k="Alternate Mobile" v={emp.alternate_mobile} />
                            <Row k="Date of Birth" v={emp.dob} />
                            <Row k="Gender" v={emp.gender} />
                            <Row k="PAN" v={<span className="font-mono">{emp.pan}</span>} />
                            <Row k="Aadhaar" v={<span className="font-mono">{emp.aadhaar}</span>} />
                        </section>
                        <section className="bg-white rounded-xl border border-slate-200 p-5">
                            <h3 className="text-sm font-semibold mb-3 text-slate-700">Employment</h3>
                            <Row k="Category" v={emp.employment_category} />
                            <Row k="Department" v={emp.department} />
                            <Row k="Designation" v={emp.designation} />
                            <Row k="Date of Joining" v={emp.date_of_joining} />
                            <Row k="Confirmation Date" v={emp.confirmation_date} />
                            <Row k="Probation End" v={emp.probation_end_date} />
                            <Row k="Notice Period" v={`${emp.notice_period_days || 0} days`} />
                            <Row k="Reporting Manager" v={emp.reporting_manager_name} />
                            <Row k="Status" v={
                                can("employee:write") ? (
                                    <select data-testid="employee-status-select" className="h-8 rounded border border-slate-300 px-2 text-xs bg-white" value={emp.status} onChange={(e) => doStatus(e.target.value)}>
                                        {(meta.employee_statuses || []).map((s) => <option key={s} value={s}>{s}</option>)}
                                    </select>
                                ) : emp.status
                            } />
                        </section>
                        <section className="bg-white rounded-xl border border-slate-200 p-5">
                            <h3 className="text-sm font-semibold mb-3 text-slate-700">Banking</h3>
                            <Row k="Bank Name" v={emp.bank_name} />
                            <Row k="Account Number" v={<span className="font-mono">{emp.bank_account_number}</span>} />
                            <Row k="IFSC" v={<span className="font-mono">{emp.bank_ifsc}</span>} />
                            <Row k="Beneficiary" v={emp.bank_beneficiary_name} />
                        </section>
                        <section className="bg-white rounded-xl border border-slate-200 p-5">
                            <h3 className="text-sm font-semibold mb-3 text-slate-700">Address & Emergency</h3>
                            <Row k="Current Address" v={emp.current_address} />
                            <Row k="Permanent Address" v={emp.permanent_address} />
                            <Row k="Emergency Name" v={emp.emergency_contact?.name} />
                            <Row k="Emergency Number" v={emp.emergency_contact?.number} />
                            <Row k="Relationship" v={emp.emergency_contact?.relationship} />
                            {emp.notes && <Row k="Notes" v={<span className="whitespace-pre-wrap">{emp.notes}</span>} />}
                        </section>
                    </div>
                )}

                {tab === "Documents" && (
                    <section className="bg-white rounded-xl border border-slate-200 p-5" data-testid="employee-documents-section">
                        {can("employee:write") && (
                            <div className="flex flex-wrap items-end gap-3 pb-4 border-b border-slate-100 mb-4">
                                <div>
                                    <Label className="text-xs">Document Type</Label>
                                    <select data-testid="doc-upload-type" className="h-9 rounded-md border border-slate-300 px-3 text-sm bg-white" value={uploadType} onChange={(e) => setUploadType(e.target.value)}>
                                        {(meta.document_types || []).map((d) => <option key={d} value={d}>{d}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <Label className="text-xs">File</Label>
                                    <Input data-testid="doc-upload-file" type="file" onChange={(e) => setUploadFile(e.target.files?.[0])} className="h-9" />
                                </div>
                                <Button data-testid="doc-upload-btn" onClick={doUpload} disabled={!uploadFile}><FileArrowUp size={14} className="mr-1" /> Upload</Button>
                            </div>
                        )}
                        <table className="w-full text-sm" data-testid="employee-documents-table">
                            <thead className="bg-slate-50 text-slate-600 text-xs">
                                <tr>
                                    <th className="text-left px-3 py-2 font-medium">Type</th>
                                    <th className="text-left px-3 py-2 font-medium">File</th>
                                    <th className="text-left px-3 py-2 font-medium">Uploaded</th>
                                    <th className="text-left px-3 py-2 font-medium">By</th>
                                    <th className="w-20" />
                                </tr>
                            </thead>
                            <tbody>
                                {(emp.documents || []).length === 0 && (
                                    <tr><td colSpan={5} className="py-8 text-center text-slate-400">No documents uploaded yet.</td></tr>
                                )}
                                {(emp.documents || []).map((d) => (
                                    <tr key={d.id} className="border-t border-slate-100">
                                        <td className="px-3 py-2"><Badge variant="outline">{d.document_type}</Badge></td>
                                        <td className="px-3 py-2 text-slate-700">
                                            <a href={d.file_url} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline">{d.file_name}</a>
                                            <div className="text-[10px] text-slate-400">{Math.round((d.size_bytes || 0) / 1024)} KB</div>
                                        </td>
                                        <td className="px-3 py-2 text-xs text-slate-500">{formatDate(d.uploaded_at)}</td>
                                        <td className="px-3 py-2 text-xs text-slate-500">{d.uploaded_by_email}</td>
                                        <td className="px-3 py-2 text-right">
                                            {can("employee:write") && (
                                                <button onClick={() => doDeleteDoc(d.id)} className="text-rose-500 hover:text-rose-700"><Trash size={14} /></button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </section>
                )}

                {tab === "Generated Documents" && (
                    <section className="bg-white rounded-xl border border-slate-200 p-5" data-testid="employee-generated-section">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 text-slate-600 text-xs">
                                <tr>
                                    <th className="text-left px-3 py-2 font-medium">Title</th>
                                    <th className="text-left px-3 py-2 font-medium">Category</th>
                                    <th className="text-left px-3 py-2 font-medium">Status</th>
                                    <th className="text-left px-3 py-2 font-medium">Generated</th>
                                    <th className="text-left px-3 py-2 font-medium">Accepted</th>
                                    <th className="text-right px-3 py-2 font-medium w-56">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(emp.generated_documents || []).length === 0 && (
                                    <tr><td colSpan={6} className="py-8 text-center text-slate-400">No documents generated yet.</td></tr>
                                )}
                                {(emp.generated_documents || []).slice().reverse().map((d) => {
                                    const tone = d.share_status === "Accepted"
                                        ? "bg-emerald-100 text-emerald-700"
                                        : d.share_status === "Declined"
                                        ? "bg-rose-100 text-rose-700"
                                        : d.share_status === "Viewed"
                                        ? "bg-amber-100 text-amber-700"
                                        : d.share_status === "Shared"
                                        ? "bg-sky-100 text-sky-700"
                                        : "bg-slate-100 text-slate-600";
                                    return (
                                    <tr key={d.id} className="border-t border-slate-100" data-testid={`gen-doc-row-${d.id}`}>
                                        <td className="px-3 py-2 text-slate-700">
                                            {d.rendered_title}
                                            <div className="text-[10px] text-slate-400 font-mono">{d.template_code}</div>
                                        </td>
                                        <td className="px-3 py-2"><Badge variant="outline">{d.category}</Badge></td>
                                        <td className="px-3 py-2"><Badge className={tone}>{d.share_status || "Generated"}</Badge></td>
                                        <td className="px-3 py-2 text-xs text-slate-500">{formatDate(d.generated_at)}</td>
                                        <td className="px-3 py-2 text-xs text-slate-500">{d.accepted_at ? formatDate(d.accepted_at) : "—"}</td>
                                        <td className="px-3 py-2 text-right">
                                            <div className="inline-flex gap-1">
                                                <Button size="sm" variant="outline" title="Preview"
                                                    data-testid={`gen-doc-preview-${d.id}`}
                                                    onClick={async () => {
                                                        try {
                                                            const { data } = await api.get(`/hr/document/${d.id}`);
                                                            setGenPreview(data);
                                                            setGenDialog(true);
                                                        } catch (err) {
                                                            toast.error(formatApiError(err, "Failed to load document"));
                                                        }
                                                    }}><Eye size={14} /></Button>
                                                <Button size="sm" variant="outline" title="Acceptance trail"
                                                    data-testid={`gen-doc-trail-${d.id}`}
                                                    onClick={() => openTrail(d.id)}><ClipboardText size={14} /></Button>
                                                {can("employee:admin") && d.share_status !== "Accepted" && (
                                                    <Button size="sm" title="Share with employee"
                                                        data-testid={`gen-doc-share-${d.id}`}
                                                        onClick={() => { setShareDialogDoc(d); setShareTokenInfo(null); setShareNote(""); }}>
                                                        <ShareNetwork size={14} />
                                                    </Button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );})}
                            </tbody>
                        </table>
                    </section>
                )}

                {tab === "Linked User" && (
                    <section className="bg-white rounded-xl border border-slate-200 p-5" data-testid="employee-linked-section">
                        {emp.linked_user_id ? (
                            <div className="space-y-3">
                                <div className="text-sm text-slate-700">This employee is linked to a system user:</div>
                                <Row k="User ID" v={<span className="font-mono">{emp.linked_user_id}</span>} />
                                <Row k="Email" v={emp.linked_user?.email} />
                                <Row k="Name" v={emp.linked_user?.name} />
                                <Row k="Role" v={emp.linked_user?.role} />
                                <Row k="Active" v={emp.linked_user?.is_active ? "Yes" : "No"} />
                                {can("employee:admin") && (
                                    <Button variant="outline" onClick={doUnlink} data-testid="employee-unlink-btn">
                                        <UserMinus size={14} className="mr-1" /> Unlink User
                                    </Button>
                                )}
                            </div>
                        ) : (
                            <div className="space-y-3">
                                <div className="text-sm text-slate-700">This employee is not linked to any system user.</div>
                                <div className="text-xs text-slate-500">Linking creates a 1:1 mapping so the employee can sign in and access Employee Self-Service (ESS). An employee may exist without a login.</div>
                                {can("employee:admin") && (
                                    <Button onClick={openLinkDialog} data-testid="employee-link-btn">
                                        <UserPlus size={14} className="mr-1" /> Link User
                                    </Button>
                                )}
                            </div>
                        )}
                    </section>
                )}

                {tab === "Audit" && (
                    <section className="bg-white rounded-xl border border-slate-200 p-5" data-testid="employee-audit-section">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 text-slate-600 text-xs">
                                <tr>
                                    <th className="text-left px-3 py-2 font-medium">When</th>
                                    <th className="text-left px-3 py-2 font-medium">Actor</th>
                                    <th className="text-left px-3 py-2 font-medium">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {audit.length === 0 && <tr><td colSpan={3} className="py-8 text-center text-slate-400">No audit events yet.</td></tr>}
                                {audit.map((r) => (
                                    <tr key={r.id} className="border-t border-slate-100">
                                        <td className="px-3 py-2 text-xs text-slate-500">{formatDate(r.created_at)}</td>
                                        <td className="px-3 py-2 text-xs text-slate-600">{r.actor_email}</td>
                                        <td className="px-3 py-2 text-xs text-slate-800">{r.action}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </section>
                )}
            </div>

            {/* Link user dialog */}
            <Dialog open={linkDialog} onOpenChange={setLinkDialog}>
                <DialogContent data-testid="link-user-dialog">
                    <DialogHeader><DialogTitle>Link System User</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <Label className="text-xs">Pick a user (1:1 mapping enforced)</Label>
                        <select data-testid="link-user-picker" className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm bg-white" value={pickedUserId} onChange={(e) => setPickedUserId(e.target.value)}>
                            <option value="">— select user —</option>
                            {users.filter((u) => u.is_active !== false).map((u) => (
                                <option key={u.id} value={u.id}>{u.name} · {u.email} ({u.role})</option>
                            ))}
                        </select>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setLinkDialog(false)}>Cancel</Button>
                        <Button onClick={doLink} disabled={!pickedUserId} data-testid="link-user-confirm">Link User</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Generate doc dialog */}
            <Dialog open={genDialog} onOpenChange={(o) => { setGenDialog(o); if (!o) { setGenPreview(null); setPickedTemplate(""); setGenIssueDate(""); setGenEffectiveDate(""); setGenPreparedBy(""); setGenSignatory(""); } }}>
                <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto" data-testid="generate-doc-dialog">
                    <DialogHeader><DialogTitle>{genPreview ? "Document Preview" : "Generate Document"}</DialogTitle></DialogHeader>
                    {!genPreview ? (
                        <div className="space-y-3">
                            <div>
                                <Label className="text-xs">Template</Label>
                                <select data-testid="gen-template-picker" className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm bg-white" value={pickedTemplate} onChange={(e) => setPickedTemplate(e.target.value)}>
                                    <option value="">— select template —</option>
                                    {templates.filter((t) => t.is_active).map((t) => <option key={t.code} value={t.code}>{t.title} ({t.category})</option>)}
                                </select>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label className="text-xs">Issue Date</Label>
                                    <Input data-testid="gen-issue-date" type="date" value={genIssueDate} onChange={(e) => setGenIssueDate(e.target.value)} />
                                </div>
                                <div>
                                    <Label className="text-xs">Effective Date</Label>
                                    <Input data-testid="gen-effective-date" type="date" value={genEffectiveDate} onChange={(e) => setGenEffectiveDate(e.target.value)} />
                                </div>
                                <div>
                                    <Label className="text-xs">Prepared By</Label>
                                    <Input data-testid="gen-prepared-by" placeholder="Defaults to current user" value={genPreparedBy} onChange={(e) => setGenPreparedBy(e.target.value)} />
                                </div>
                                <div>
                                    <Label className="text-xs">Authorized Signatory</Label>
                                    <Input data-testid="gen-signatory" placeholder="e.g. Rahul Galu, Director" value={genSignatory} onChange={(e) => setGenSignatory(e.target.value)} />
                                </div>
                            </div>
                            <p className="text-[11px] text-slate-400">Template placeholders <code>{`{{document.issue_date}}`}</code>, <code>{`{{document.effective_date}}`}</code>, <code>{`{{document.prepared_by}}`}</code>, <code>{`{{document.authorized_signatory}}`}</code> will resolve to the values entered above. If left blank, sensible defaults apply (Issue Date → today; Prepared By → your name).</p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            <div className="text-xs text-slate-500">Template: <span className="font-mono">{genPreview.template_code}</span> · v{genPreview.template_version} · generated {formatDate(genPreview.generated_at)}</div>
                            {genPreview.unresolved_placeholders?.length > 0 && (
                                <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                                    Unresolved placeholders: {genPreview.unresolved_placeholders.join(", ")}
                                </div>
                            )}
                            <div className="border border-slate-200 rounded-lg p-4 bg-slate-50 max-h-[50vh] overflow-y-auto" data-testid="gen-doc-preview" dangerouslySetInnerHTML={{ __html: genPreview.rendered_html }} />
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setGenDialog(false)}>Close</Button>
                        {!genPreview && <Button onClick={doGenerate} disabled={!pickedTemplate} data-testid="gen-doc-confirm">Generate</Button>}
                        {genPreview && (
                            <Button variant="outline" onClick={() => {
                                const w = window.open("", "_blank");
                                if (w) {
                                    w.document.write(`<html><head><title>${genPreview.rendered_title}</title></head><body>${genPreview.rendered_html}</body></html>`);
                                    w.document.close();
                                    w.print();
                                }
                            }} data-testid="gen-doc-print">Print / Save PDF</Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Iter 81.1 — Share document dialog */}
            <Dialog open={!!shareDialogDoc} onOpenChange={(o) => { if (!o) { setShareDialogDoc(null); setShareNote(""); setShareTokenInfo(null); } }}>
                <DialogContent className="max-w-lg" data-testid="share-doc-dialog">
                    <DialogHeader><DialogTitle>Share Document with Employee</DialogTitle></DialogHeader>
                    {!shareTokenInfo ? (
                        <div className="space-y-3">
                            <div className="text-sm text-slate-700">{shareDialogDoc?.rendered_title}</div>
                            <div className="text-xs text-slate-500">Choose how to share. Both options record the share in the audit trail.</div>
                            <div>
                                <Label className="text-xs">Internal note (optional)</Label>
                                <Textarea data-testid="share-note" rows={2} value={shareNote} onChange={(e) => setShareNote(e.target.value)} placeholder="e.g. Please review and sign before Friday." />
                            </div>
                            <div className="rounded-lg border border-slate-200 p-3 text-xs space-y-1.5 bg-slate-50">
                                <div className="font-semibold text-slate-700">Option A · ESS Login</div>
                                <div className="text-slate-600">Employee must already be linked to a system user. They will see this document in their <em>My Profile</em> ESS portal.</div>
                            </div>
                            <div className="rounded-lg border border-indigo-200 p-3 text-xs space-y-1.5 bg-indigo-50/50">
                                <div className="font-semibold text-indigo-800">Option B · Secure Tokenised Link</div>
                                <div className="text-slate-600">Generates a one-time, expiring URL the candidate can use to review + accept WITHOUT signing in. Available for Offer Letter, Appointment Letter and NDA categories.</div>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-3" data-testid="share-token-result">
                            <div className="text-sm text-emerald-700 font-medium">✓ Secure link generated</div>
                            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs">
                                <div className="font-mono break-all text-slate-700" data-testid="share-token-url">{shareTokenInfo.url}</div>
                                <div className="text-[10px] text-slate-500 mt-2">Expires: {shareTokenInfo.expires_at}</div>
                            </div>
                            <div className="text-xs text-slate-500">Share this link with the candidate. The link is single-use — once accepted or declined, it stops working.</div>
                            <Button size="sm" variant="outline" data-testid="share-token-copy" onClick={() => { navigator.clipboard.writeText(shareTokenInfo.url); toast.success("Link copied to clipboard"); }} className="gap-1"><Copy size={14} /> Copy Link</Button>
                        </div>
                    )}
                    <DialogFooter>
                        {!shareTokenInfo && (
                            <>
                                <Button variant="outline" onClick={() => { setShareDialogDoc(null); setShareNote(""); }}>Cancel</Button>
                                <Button variant="outline" onClick={() => doShare(false)} data-testid="share-ess-btn">
                                    <ShareNetwork size={14} className="mr-1" /> Share via ESS
                                </Button>
                                <Button onClick={() => doShare(true)} data-testid="share-token-btn">
                                    <LinkIcon size={14} className="mr-1" /> Generate Secure Link
                                </Button>
                            </>
                        )}
                        {shareTokenInfo && <Button onClick={() => { setShareDialogDoc(null); setShareTokenInfo(null); setShareNote(""); }}>Done</Button>}
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Iter 81.1 — Acceptance trail dialog */}
            <Dialog open={!!trailDoc} onOpenChange={(o) => { if (!o) { setTrailDoc(null); setTrailData(null); } }}>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="trail-dialog">
                    <DialogHeader><DialogTitle>Acceptance Trail</DialogTitle></DialogHeader>
                    {!trailData ? (
                        <div className="text-sm text-slate-500">Loading…</div>
                    ) : (
                        <div className="space-y-3 text-sm">
                            <div className="grid grid-cols-2 gap-2 text-xs">
                                <Row k="Status" v={<Badge>{trailData.document.share_status}</Badge>} />
                                <Row k="Shared" v={trailData.document.shared_at ? `${formatDate(trailData.document.shared_at)} by ${trailData.document.shared_by_email}` : "Not yet"} />
                                <Row k="First viewed" v={trailData.document.first_viewed_at ? formatDate(trailData.document.first_viewed_at) : "Not yet"} />
                                <Row k="Last viewed" v={trailData.document.last_viewed_at ? formatDate(trailData.document.last_viewed_at) : "—"} />
                                <Row k="Views" v={trailData.document.view_count || 0} />
                                <Row k="Accepted" v={trailData.document.accepted_at ? `${formatDate(trailData.document.accepted_at)} (${trailData.document.acceptance_mode})` : "—"} />
                                <Row k="Declined" v={trailData.document.declined_at ? formatDate(trailData.document.declined_at) : "—"} />
                                {trailData.document.decline_reason && <Row k="Decline reason" v={trailData.document.decline_reason} />}
                            </div>
                            <div>
                                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mt-3 mb-2">Acceptance Records</div>
                                {trailData.acceptances.length === 0 ? (
                                    <div className="text-xs text-slate-400">No acceptance records yet.</div>
                                ) : (
                                    <table className="w-full text-xs">
                                        <thead className="bg-slate-50">
                                            <tr><th className="text-left px-2 py-1">When</th><th className="text-left px-2 py-1">Outcome</th><th className="text-left px-2 py-1">Mode</th><th className="text-left px-2 py-1">By</th><th className="text-left px-2 py-1">IP</th><th className="text-left px-2 py-1">Remarks</th></tr>
                                        </thead>
                                        <tbody>
                                            {trailData.acceptances.map((a) => (
                                                <tr key={a.id} className="border-t border-slate-100">
                                                    <td className="px-2 py-1 text-slate-500">{formatDate(a.accepted_at)}</td>
                                                    <td className="px-2 py-1"><Badge className={a.outcome === "Accepted" ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}>{a.outcome}</Badge></td>
                                                    <td className="px-2 py-1 text-slate-600">{a.mode}</td>
                                                    <td className="px-2 py-1 text-slate-600">{a.accepted_by_email || "anonymous"}</td>
                                                    <td className="px-2 py-1 font-mono text-[10px] text-slate-500">{a.ip || "—"}</td>
                                                    <td className="px-2 py-1 text-slate-600">{a.remarks || "—"}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                            {trailData.tokens?.length > 0 && (
                                <div>
                                    <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mt-3 mb-2">Issued Tokens</div>
                                    <table className="w-full text-xs">
                                        <thead className="bg-slate-50"><tr><th className="text-left px-2 py-1">When</th><th className="text-left px-2 py-1">By</th><th className="text-left px-2 py-1">Expires</th><th className="text-left px-2 py-1">Used</th></tr></thead>
                                        <tbody>
                                            {trailData.tokens.map((t) => (
                                                <tr key={t.id} className="border-t border-slate-100">
                                                    <td className="px-2 py-1 text-slate-500">{formatDate(t.created_at)}</td>
                                                    <td className="px-2 py-1 text-slate-600">{t.created_by_email}</td>
                                                    <td className="px-2 py-1 text-slate-500">{formatDate(t.expires_at)}</td>
                                                    <td className="px-2 py-1 text-slate-600">{t.used_at ? `${t.used_outcome} ${formatDate(t.used_at)}` : "Unused"}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}
                    <DialogFooter>
                        <Button onClick={() => { setTrailDoc(null); setTrailData(null); }}>Close</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
